// Boxed-expression evaluation. Dispatches on the moddle expression's $type
// and recurses through the closure passed to every branch. Keeps the FEEL
// env threaded through — each branch extends it where the DMN semantics
// require (context siblings, function bodies, for loops on relations).

import { evaluate as feelEvaluate } from '../feel/Evaluator';
import { evalDecisionTable } from './evalDecisionTable';
import { evaluateMlDecision } from '../feel/evaluateMlDecision';
import { getMlInvoker } from './defaultMlInvoker';

export class ExpressionEvalError extends Error {
  constructor(msg) { super(`[simulator/expr] ${msg}`); }
}

export function evalExpression(expr, env, ctx = null) {
  if (!expr) return null;
  const type = expr.$type;
  switch (type) {
    case 'dmn:LiteralExpression': return evalLiteral(expr, env);
    case 'dmn:Context':           return evalContext(expr, env);
    case 'dmn:List':              return evalList(expr, env);
    case 'dmn:Relation':          return evalRelation(expr, env);
    case 'dmn:Invocation':        return evalInvocation(expr, env);
    case 'dmn:FunctionDefinition':return evalFunctionDefinition(expr, env);
    case 'dmn:DecisionTable':     return evalDecisionTable(expr, env).result;
    case 'epx:MachineLearningDecision':
      // ML evaluation is async — returns a Promise. The simulator awaits it.
      return evaluateMlDecision(expr, env, {
        evalFeel: (text, scope) => feelEvaluate(text, scope || env),
        mlInvoker: (ctx && ctx.mlInvoker) || getMlInvoker(),
      });
    default:
      throw new ExpressionEvalError(`unsupported expression type '${type}'`);
  }
}

function evalLiteral(expr, env) {
  const text = expr.text;
  if (text == null || text === '') return null;
  return feelEvaluate(text, env);
}

function evalContext(expr, env) {
  const entries = expr.contextEntry || [];
  const extended = { ...env };
  let resultValue = null;
  const ctxBag = {};
  entries.forEach((entry) => {
    const value = evalExpression(entry.value, extended);
    const name = entry.variable && entry.variable.name;
    if (name) {
      extended[name] = value;
      ctxBag[name] = value;
    } else {
      // result entry (no variable) → context evaluates to this value
      resultValue = value;
    }
  });
  // DMN 1.3: if there's a result entry use it, otherwise return the bag
  return resultValue != null || entries.some((e) => !(e.variable && e.variable.name))
    ? resultValue
    : ctxBag;
}

function evalList(expr, env) {
  const items = expr.elements || [];
  return items.map((it) => evalExpression(it, env));
}

function evalRelation(expr, env) {
  const cols = (expr.column || []).map((c) => c.name);
  const rows = expr.row || [];
  return rows.map((row) => {
    const cells = row.elements || [];
    const obj = {};
    cols.forEach((name, i) => {
      obj[name] = cells[i] != null ? evalExpression(cells[i], env) : null;
    });
    return obj;
  });
}

function evalInvocation(expr, env) {
  const called = expr.calledFunction;
  if (!called) throw new ExpressionEvalError('invocation has no calledFunction');
  const fn = evalExpression(called, env);
  if (typeof fn !== 'function') {
    throw new ExpressionEvalError(`calledFunction did not resolve to a callable`);
  }
  const bindings = expr.binding || [];
  const argsByName = {};
  const positional = [];
  bindings.forEach((b) => {
    const name = b.parameter && b.parameter.name;
    const value = b.bindingFormula ? evalExpression(b.bindingFormula, env) : null;
    if (name) argsByName[name] = value;
    positional.push(value);
  });
  // Prefer named invocation if the function declares __feelParams, else positional.
  if (fn.__feelParams && fn.__feelParams.length) {
    const args = fn.__feelParams.map((p) =>
      Object.prototype.hasOwnProperty.call(argsByName, p) ? argsByName[p] : undefined
    );
    return fn(...args);
  }
  return fn(...positional);
}

export function evalFunctionDefinition(expr, env) {
  const params = (expr.formalParameter || []).map((p) => p.name);
  const body = expr.body;
  const fn = (...args) => {
    const extended = { ...env };
    params.forEach((p, i) => { extended[p] = args[i]; });
    return evalExpression(body, extended);
  };
  fn.__feelParams = params;
  return fn;
}
